import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw, X } from 'lucide-react';

import { t, type StringKey } from '../lib/i18n';
import { placedFromQuad, quadFromPlacedCorners } from '../lib/crop-quad';
import type { ImagePoint, ManualQuad } from '../lib/types';
import { useAppStore } from '../store';
import { useObjectUrl } from './useObjectUrl';

interface CropModalProps {
  /** The session's original uploaded file. Decoded once into an object URL. */
  file: File;
  /** Filename for the modal heading; falls back to the file's own name. */
  filename?: string;
  /** User clicked Apply with the four corners (in original-image pixels). */
  onApply: (corners: ManualQuad) => void;
  /** User clicked Cancel / pressed Escape / clicked the backdrop. */
  onClose: () => void;
  /**
   * Auto-detected (or previously applied manual) crop quad in original-
   * image pixel coords. When present, the four corners spawn pre-placed
   * on it — fixing a near-miss auto-crop becomes "drag the one bad
   * corner" instead of four taps from scratch. The reset button still
   * clears to empty for a full redo.
   */
  initialQuad?: ManualQuad;
}

/**
 * Manual-crop fallback. Used when the auto-detector lands on the wrong
 * quad — common on backgrounds where the receipt's edge isn't the
 * dominant gradient (textured wood, near-paper-coloured cement, scanner
 * vignettes).
 *
 * Corner placement is click-to-place, in a PRESCRIBED order: the user
 * taps the receipt's bottom-left corner first, then continues clockwise
 * (top-left → top-right → bottom-right), guided by an instruction
 * overlay. Placed points stay draggable for fine-tuning. Two reasons
 * for the fixed order instead of free clicking + auto-sort:
 *   - `warpFromCorners` maps quad corner i to output corner i verbatim
 *     (no re-ordering in the worker), so the order IS the contract;
 *   - the user's order tells us the receipt's own orientation — a
 *     receipt lying sideways in the photo warps straight to upright
 *     portrait, which the auto-detector can only guess at (its 180°
 *     ambiguity needs an OCR retry).
 *
 * Coordinate spaces:
 *   - "natural"  — the source image's intrinsic pixel grid, what the
 *                  worker wants to receive
 *   - "display"  — CSS pixels of the rendered <img>, where the user
 *                  actually taps. Scaled uniformly from natural by the
 *                  fit-to-viewport sizing below.
 *
 * Corners are stored in display-space while editing (no rounding error
 * accumulation across drags) and converted to natural coords at submit
 * time.
 *
 * Pointer events (not mouse + touch separately) so a single drag handler
 * works on desktop, iOS, and Android. While the modal is open the BODY
 * is scroll-locked with the position:fixed technique — without it iOS
 * Safari lets touch drags scroll the page behind the overlay (the
 * horizontal scrollbar users saw mid-crop), fighting corner placement.
 */

const HANDLE_RADIUS_PX = 16;

/**
 * Placement order: receipt bottom-left, then clockwise. Index into this
 * array = how many corners are already placed = which corner is next.
 */
const STEP_KEYS: readonly StringKey[] = [
  'crop.tapBottomLeft',
  'crop.tapTopLeft',
  'crop.tapTopRight',
  'crop.tapBottomRight',
];

export function CropModal({ file, filename, onApply, onClose, initialQuad }: CropModalProps) {
  const lang = useAppStore((s) => s.lang);
  // Object URL for the rendered <img>. Created once when the modal
  // mounts, revoked on unmount. The component re-renders on corner
  // drags; recreating the URL on each render would leak.
  const objectUrl = useObjectUrl(file);

  // Natural-space dimensions, populated from the <img>'s onLoad. Until
  // then corner placement can't start — we don't know how big the
  // image actually is. Using the rendered <img>'s naturalWidth/Height
  // covers HEIC, PDF page rendering (which becomes a JPEG before this
  // modal sees it) and any other format the browser can decode.
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);

  // Container size measured via ResizeObserver. The image's display
  // size (display-space) is computed from this so the handles stay
  // aligned with the visually-rendered image as the viewport resizes
  // (e.g., orientation change on phones).
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{ w: number; h: number } | null>(null);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (el === null) return;
    const update = (): void => {
      const r = el.getBoundingClientRect();
      setContainerSize({ w: r.width, h: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Scroll-lock the page while the modal is open. iOS Safari ignores a
  // plain overflow:hidden on body for touch scrolling; the
  // position:fixed technique is the reliable variant. Restores the
  // exact scroll position on unmount.
  useEffect(() => {
    const body = document.body;
    const scrollY = window.scrollY;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      overflow: body.style.overflow,
    };
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.overflow = 'hidden';
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Display dimensions: scale natural to fit inside the container while
  // preserving aspect ratio.
  const display = useMemo(() => {
    if (natural === null || containerSize === null) return null;
    const scale = Math.min(
      containerSize.w / natural.w,
      containerSize.h / natural.h,
    );
    return {
      w: natural.w * scale,
      h: natural.h * scale,
      scale,
      // Centre the display rect within the container.
      offsetX: (containerSize.w - natural.w * scale) / 2,
      offsetY: (containerSize.h - natural.h * scale) / 2,
    };
  }, [natural, containerSize]);

  // Corners in display-space coords (offsets relative to the display
  // image's top-left, NOT the container's), in TAP order: BL, TL, TR,
  // BR. Starts EMPTY — points spawn where the user taps, guided by the
  // instruction overlay, instead of pre-seeding the image's own corners
  // (which forced four drags across the whole frame every time).
  const [corners, setCorners] = useState<ImagePoint[]>([]);

  // Pre-seed the corners from the detected quad the first time the display
  // geometry is known (it needs `display.scale`, so it can't happen in the
  // useState initialiser). Once only — after the user hits reset, the
  // seeded points must not respawn when a viewport resize changes
  // `display`. Clamped into the display rect: detected corners sit within
  // the image by construction, but a defensive clamp costs nothing.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (initialQuad === undefined || display === null) return;
    seededRef.current = true;
    setCorners(
      placedFromQuad(initialQuad, display.scale).map((p) => ({
        x: clamp(p.x, 0, display.w),
        y: clamp(p.y, 0, display.h),
      })),
    );
  }, [initialQuad, display]);

  // Active drag state. cornerIdx is which handle the user grabbed;
  // pointerId tracks the specific pointer so a second touch doesn't
  // hijack the drag. Cleared on pointerup / pointercancel.
  const dragRef = useRef<{ cornerIdx: number; pointerId: number } | null>(null);

  const handlePointerDown = (
    e: React.PointerEvent<HTMLDivElement>,
    cornerIdx: number,
  ): void => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { cornerIdx, pointerId: e.pointerId };
    // Capture so subsequent move events come to this element even if
    // the cursor leaves the handle's hit area mid-drag.
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    if (drag === null || drag.pointerId !== e.pointerId) return;
    if (display === null) return;
    const containerEl = containerRef.current;
    if (containerEl === null) return;
    const rect = containerEl.getBoundingClientRect();
    // Convert client coords → container coords → display-rect coords
    // (subtracting the centring offset). Clamp to the display rect so
    // a corner can never sit outside the visible image.
    const xInContainer = e.clientX - rect.left;
    const yInContainer = e.clientY - rect.top;
    const x = clamp(xInContainer - display.offsetX, 0, display.w);
    const y = clamp(yInContainer - display.offsetY, 0, display.h);
    setCorners((prev) => {
      if (drag.cornerIdx >= prev.length) return prev;
      const next = [...prev];
      next[drag.cornerIdx] = { x, y };
      return next;
    });
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (dragRef.current?.pointerId === e.pointerId) {
      dragRef.current = null;
    }
  };

  // Tap-to-place: a click on the empty crop area (not on a handle —
  // those have their own pointer handlers and a different event target)
  // places the next corner. Ignored once all four are down; from then
  // on only dragging adjusts.
  const handlePlace = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (e.target !== e.currentTarget) return;
    if (display === null) return;
    if (corners.length >= 4) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = clamp(e.clientX - rect.left - display.offsetX, 0, display.w);
    const y = clamp(e.clientY - rect.top - display.offsetY, 0, display.h);
    setCorners((prev) => (prev.length >= 4 ? prev : [...prev, { x, y }]));
  };

  // Escape closes. Click on the backdrop (not the modal panel) closes too.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const apply = (): void => {
    if (display === null) return;
    const result = quadFromPlacedCorners(corners, 1 / display.scale);
    if (result === null) return;
    onApply(result);
  };

  // Visual overlay connecting the placed corners: a growing polyline
  // while placing, a closed polygon once all four are down. SVG renders
  // in display-space; offsetX/Y push it into the centred image rect.
  const pointsAttr = corners.map((c) => `${c.x},${c.y}`).join(' ');
  const placingDone = corners.length === 4;
  const stepKey = STEP_KEYS[corners.length];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t(lang, 'crop.title')}
      className="fixed inset-0 z-50 flex flex-col overscroll-none bg-slate-950/80 backdrop-blur-sm dark:bg-black/80"
      onClick={(e) => {
        // Backdrop click — only close when the click is on the overlay
        // itself, not on a child of the modal panel below.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-slate-700 bg-slate-900 px-4 py-3 text-slate-100">
        <div>
          <h2 className="text-base font-semibold">{t(lang, 'crop.title')}</h2>
          <p className="text-xs text-slate-400">
            {t(lang, 'crop.description')}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t(lang, 'crop.close')}
          className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-100"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <div
        ref={containerRef}
        className="relative flex-1 select-none overflow-hidden touch-none"
        onClick={handlePlace}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <img
          src={objectUrl}
          alt={filename ?? file.name}
          className="pointer-events-none absolute inset-0 m-auto select-none"
          style={
            display === null
              ? undefined
              : { width: display.w, height: display.h }
          }
          onLoad={(e) => {
            const img = e.currentTarget;
            setNatural({ w: img.naturalWidth, h: img.naturalHeight });
          }}
          draggable={false}
        />

        {/* Instruction overlay: which corner to tap next. Gone once all
            four are placed — the header description covers fine-tuning. */}
        {display !== null && stepKey !== undefined && (
          <div
            role="status"
            aria-live="polite"
            className="pointer-events-none absolute left-1/2 top-4 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-indigo-600/90 px-4 py-2 text-sm font-medium text-white shadow-lg ring-1 ring-white/25"
          >
            {t(lang, stepKey)}
          </div>
        )}

        {display !== null && corners.length > 0 && (
          <>
            <svg
              className="pointer-events-none absolute"
              style={{
                left: display.offsetX,
                top: display.offsetY,
                width: display.w,
                height: display.h,
              }}
              viewBox={`0 0 ${display.w} ${display.h}`}
            >
              {placingDone ? (
                <polygon
                  points={pointsAttr}
                  fill="rgba(99, 102, 241, 0.15)"
                  stroke="rgb(129, 140, 248)"
                  strokeWidth={2}
                />
              ) : (
                <polyline
                  points={pointsAttr}
                  fill="none"
                  stroke="rgb(129, 140, 248)"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                />
              )}
            </svg>
            {corners.map((c, i) => (
              <div
                key={i}
                onPointerDown={(e) => handlePointerDown(e, i)}
                role="button"
                tabIndex={0}
                aria-label={t(lang, 'crop.cornerAria', { n: i + 1 })}
                style={{
                  left: display.offsetX + c.x - HANDLE_RADIUS_PX,
                  top: display.offsetY + c.y - HANDLE_RADIUS_PX,
                  width: HANDLE_RADIUS_PX * 2,
                  height: HANDLE_RADIUS_PX * 2,
                }}
                className="absolute flex cursor-grab items-center justify-center rounded-full border-2 border-indigo-300 bg-indigo-500/70 text-xs font-bold text-white shadow-md ring-2 ring-white/20 transition-transform active:scale-110 active:cursor-grabbing"
              >
                {i + 1}
              </div>
            ))}
          </>
        )}
      </div>

      <div className="flex flex-shrink-0 items-center justify-between gap-2 border-t border-slate-700 bg-slate-900 px-4 py-3">
        <button
          type="button"
          onClick={() => setCorners([])}
          disabled={corners.length === 0}
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-slate-800 hover:text-slate-100 disabled:cursor-not-allowed disabled:text-slate-600 disabled:hover:bg-transparent"
        >
          <RotateCcw className="h-4 w-4" aria-hidden="true" />
          {t(lang, 'crop.reset')}
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-slate-800 hover:text-slate-100"
          >
            {t(lang, 'crop.cancel')}
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={!placingDone}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-indigo-500 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400"
          >
            {t(lang, 'crop.apply')}
          </button>
        </div>
      </div>
    </div>
  );
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(Math.max(v, lo), hi);
}
